import {
    BinaryExpr,
    ComparisonExpr,
    ConcatExpr,
    InvokeFunctionExpr,
} from '@mintdata-wsp/common/controllers/spreadsheet/formula/ast/FormulaASTNodes';
import {
    BinaryOpType,
    CompareOpType,
    FunctionNameLiteral,
    IFormulaNode,
} from '@mintdata-wsp/common/controllers/spreadsheet/formula/ast/FormulaDefinitions';
import {FormulaValueCoercionUtils} from '@mintdata-wsp/common/controllers/spreadsheet/formula/FormulaValueCoercionUtils';
import {SheetAPIImpl} from '@mintdata-wsp/common/controllers/spreadsheet/formula/functionAPIs/SheetAPIImpl';
import {FunctionInvoker} from '@mintdata-wsp/common/controllers/spreadsheet/formula/invoke/FunctionInvoker';
import {FunctionUtil} from '@mintdata-wsp/common/controllers/spreadsheet/formula/invoke/FunctionUtil';
import {ICellIOContext} from '@mintdata-wsp/common/controllers/spreadsheet/formula/operations/eval/CellIOContext';
import {EvalStack} from '@mintdata-wsp/common/controllers/spreadsheet/formula/operations/eval/EvalStack';
import {IFunctionResolver} from '@mintdata-wsp/common/controllers/spreadsheet/formula/operations/eval/FunctionResolver';
import {ISheetIOContext} from '@mintdata-wsp/common/controllers/spreadsheet/formula/operations/eval/SheetIOContext';
import {
    INamingContext,
    ISheetValue,
    NamedArguments,
    SimpleNamingContext
} from '@mintdata/mint-kit/api/functions/SheetAPI';
import {area, mk} from '@mintdata/mint-kit/api/log/mk';
import {Type} from '@mintdata/mint-kit/api/types/Type';
import {IValue} from '@mintdata/mint-kit/api/types/Value';
import {ABoolean} from '@mintdata/mint-kit/api/values/ABoolean';
import {ANumber} from '@mintdata/mint-kit/api/values/ANumber';
import {AString} from '@mintdata/mint-kit/api/values/AString';
import {EvalErrors} from '@mintdata/mint-kit/api/values/errors/EvalErrors';
import {CoercionUtils} from '@mintdata/mint-kit/impl/coercionUtils/CoercionUtils';

export class EvalLogic
{
    protected readonly _stack:EvalStack;
    protected readonly _sheetIO:ISheetIOContext;
    protected readonly _cellIO:ICellIOContext;
    protected readonly _functionResolver:IFunctionResolver;
    protected readonly _context:SheetAPIImpl;
    protected readonly _namedArguments:NamedArguments;
    protected readonly _javaEvalContext:any;

    constructor(stack:EvalStack,
                sheetIO:ISheetIOContext,
                cellIO:ICellIOContext,
                functionResolver:IFunctionResolver,
                context:SheetAPIImpl,
                namedArguments?:NamedArguments,
                javaEvalContext?:any)
    {
        this._stack = stack;
        this._sheetIO = sheetIO;
        this._cellIO = cellIO;
        this._functionResolver = functionResolver;
        this._context = context;
        this._javaEvalContext = javaEvalContext;
    }

    static getNamingContext():INamingContext
    {
        return new SimpleNamingContext();
    }

    public invokeFunction(invokeFunc:InvokeFunctionExpr, args:IFormulaNode[], argValues:IValue[]):Promise<IValue>
    {
        const literal:FunctionNameLiteral = FunctionUtil.parseFunctionName(invokeFunc.functionFullName.value);

        mk.debug(area.Formula, `[toValue] invokeFunction: ${literal.toString()}`);

        return FunctionInvoker.invokeFunction(
            this._context,
            this._functionResolver,
            EvalLogic.getNamingContext(),
            literal.namespace,
            literal.name,
            args,
            argValues,
            this._javaEvalContext
        );
    }

    private _numberToString(value:number):string
    {
        const sign:string = +value < 0 ? '-' : '';
        const valueString:string = value.toString();

        if( !/e/i.test(valueString) )
            return valueString;

        const [lead, decimal, pow] = valueString
            .replace(/^-/, '')
            .replace(/^([0-9]+)(e.*)/, '$1.$2')
            .split(/e|\./);

        return +pow < 0
            ? sign + '0.' + '0'.repeat(Math.max(Math.abs(+pow) - 1 || 0, 0)) + lead + decimal
            : sign + lead + (+pow >= decimal.length
            ? (decimal + '0'.repeat(Math.max(+pow - decimal.length || 0, 0)))
            : (decimal.slice(0, +pow) + '.' + decimal.slice(+pow)));
    }

    private _getFractionMultiplier(value:number):number
    {
        if( !value ) return 1;

        const valueString:string = this._numberToString(value);
        const decimalSymbolIndex:number = valueString.indexOf('.');
        const fractionDigits:number = decimalSymbolIndex !== -1 && decimalSymbolIndex !== valueString.length - 1
            ? valueString.length - decimalSymbolIndex - 1
            : 0;

        return Math.pow(10, fractionDigits);
    }

    public binary(lhsValue:IValue | ISheetValue, rhsValue:IValue | ISheetValue, binaryOp:BinaryExpr):IValue
    {
        const lValue:IValue = FormulaValueCoercionUtils.ConvertToIValue(
            this._context.getSheetContext(),
            EvalLogic.getNamingContext(),
            lhsValue
        ) as IValue;
        const rValue:IValue = FormulaValueCoercionUtils.ConvertToIValue(
            this._context.getSheetContext(),
            EvalLogic.getNamingContext(),
            rhsValue
        ) as IValue;

        const opType = binaryOp.opType;

        if( !lValue )
        {
            mk.debug(area.Formula, `[toValue] binaryExpr: ${binaryOp.toString()} => ${lhsValue}, ${rhsValue}, ${opType}`);
            return EvalErrors.Value;
        }

        if( lValue.isError )
        {
            mk.debug(area.Formula, `[toValue] binaryExpr: ${binaryOp.toString()} => ${lhsValue}, ${rhsValue}, ${opType}`);
            return lValue;
        }

        if( !rValue )
        {
            mk.debug(area.Formula, `[toValue] binaryExpr: ${binaryOp.toString()} => ${lhsValue}, ${rhsValue}, ${opType}`);
            return EvalErrors.Value;
        }

        if( rValue.isError )
        {
            mk.debug(area.Formula, `[toValue] binaryExpr: ${binaryOp.toString()} => ${lhsValue}, ${rhsValue}, ${opType}`);
            return rValue;
        }

        const lhsAsNumber:IValue = CoercionUtils.coerce(lValue, Type.Number, null);
        const rhsAsNumber:IValue = CoercionUtils.coerce(rValue, Type.Number, null);

        if( lhsAsNumber.isError )
        {
            mk.debug(area.Formula, `[toValue] binaryExpr: ${binaryOp.toString()} => ${lhsAsNumber}`);
            return lhsAsNumber;
        }

        if( rhsAsNumber.isError )
        {
            mk.debug(area.Formula, `[toValue] binaryExpr: ${binaryOp.toString()} => ${rhsAsNumber}`);
            return rhsAsNumber;
        }

        const numberA:number = (lhsAsNumber as ANumber).value;
        const numberB:number = (rhsAsNumber as ANumber).value;

        let value:IValue;

        if( opType === BinaryOpType.Add )
        {
            const multiplier:number = Math.max(
                this._getFractionMultiplier(numberA),
                this._getFractionMultiplier(numberB)
            );
            value = ANumber.fromNumber(Math.floor(numberA * multiplier + numberB * multiplier) / multiplier);
        }
        else if( opType === BinaryOpType.Subtract )
        {
            const multiplier:number = Math.max(
                this._getFractionMultiplier(numberA),
                this._getFractionMultiplier(numberB)
            );
            value = ANumber.fromNumber(Math.floor(numberA * multiplier - numberB * multiplier) / multiplier);
        }
        else if( opType === BinaryOpType.Multiply )
            value = ANumber.fromNumber(numberA * numberB);
        else if( opType === BinaryOpType.Divide )
        {
            if( numberB == 0 )
                value = EvalErrors.Div0;
            else
                value = ANumber.fromNumber(numberA / numberB);
        }
        else if( opType === BinaryOpType.Power )
            value = ANumber.fromNumber(Math.pow(numberA, numberB));
        else
        {
            mk.error(area.Formula, `[toValue] binaryExpr: ${binaryOp.toString()} => unknown ${opType}`);
            value = EvalErrors.UnknownBinaryOperator;
        }

        mk.debug(area.Formula, `[toValue] binaryExpr: ${binaryOp.toString()} => ${value}`);
        return value;
    }

    public comparison(lhsValue:IValue | ISheetValue, rhsValue:IValue | ISheetValue, comparison:ComparisonExpr):IValue
    {
        const lValue:IValue = FormulaValueCoercionUtils.ConvertToIValue(
            this._context.getSheetContext(),
            EvalLogic.getNamingContext(),
            lhsValue
        ) as IValue;
        const rValue:IValue = FormulaValueCoercionUtils.ConvertToIValue(
            this._context.getSheetContext(),
            EvalLogic.getNamingContext(),
            rhsValue
        ) as IValue;

        const opType = comparison.opType;

        if( !lValue )
        {
            mk.debug(area.Formula, `[toValue] comparisonExpr: ${comparison.toString()} => ${lhsValue}, ${rhsValue}, ${opType}`);
            return EvalErrors.Value;
        }

        if( lValue.isError )
        {
            mk.debug(area.Formula, `[toValue] comparisonExpr: ${comparison.toString()} => ${lhsValue}, ${rhsValue}, ${opType}`);
            return lValue;
        }

        if( !rValue )
        {
            mk.debug(area.Formula, `[toValue] comparisonExpr: ${comparison.toString()} => ${lhsValue}, ${rhsValue}, ${opType}`);
            return EvalErrors.Value;
        }

        if( rValue.isError )
        {
            mk.debug(area.Formula, `[toValue] comparisonExpr: ${comparison.toString()} => ${lhsValue}, ${rhsValue}, ${opType}`);
            return rValue;
        }

        let rhsCompareValue:IValue = rValue;

        if( !lValue.isEmpty )
        {
            rhsCompareValue = CoercionUtils.coerce(rValue, lValue.getType(), null);

            if( rhsCompareValue.isError )
            {
                mk.debug(area.Formula, `[toValue] comparisonExpr: ${comparison.toString()} => ${rhsCompareValue}`);
                return rhsCompareValue;
            }
        }

        const compareResult:number = lValue.compareTo(rhsCompareValue);

        let value:IValue;

        if( opType === CompareOpType.Equal )
            value = (compareResult == 0) ? ABoolean.true() : ABoolean.false();
        else if( opType === CompareOpType.NotEqual )
            value = (compareResult != 0) ? ABoolean.true() : ABoolean.false();
        else if( opType === CompareOpType.Gt )
            value = (compareResult > 0) ? ABoolean.true() : ABoolean.false();
        else if( opType === CompareOpType.Lt )
            value = (compareResult < 0) ? ABoolean.true() : ABoolean.false();
        else if( opType === CompareOpType.Ge )
            value = (compareResult >= 0) ? ABoolean.true() : ABoolean.false();
        else if( opType === CompareOpType.Le )
            value = (compareResult <= 0) ? ABoolean.true() : ABoolean.false();
        else
        {
            mk.debug(area.Formula, `[toValue] comparisonExpr: ${comparison.toString()} => unknown ${opType}`);
            value = EvalErrors.UnknownBinaryOperator;
        }

        mk.debug(area.Formula, `[toValue] comparisonExpr: ${comparison.toString()} => ${lhsValue.getType()}:[${lhsValue}] ${comparison.opType} ${rhsCompareValue.getType()}:[${rhsCompareValue}] => ${value}`);
        return value;
    }

    public concat(lhsValue:IValue | ISheetValue, rhsValue:IValue | ISheetValue, concatOp:ConcatExpr):IValue
    {
        const lValue:IValue = FormulaValueCoercionUtils.ConvertToIValue(
            this._context.getSheetContext(),
            EvalLogic.getNamingContext(),
            lhsValue
        ) as IValue;
        const rValue:IValue = FormulaValueCoercionUtils.ConvertToIValue(
            this._context.getSheetContext(),
            EvalLogic.getNamingContext(),
            rhsValue
        ) as IValue;

        if( !lValue )
        {
            mk.debug(area.Formula, `[toValue] concatExpr: ${concatOp.toString()} => ${lhsValue}, ${rhsValue}`);
            return EvalErrors.Value;
        }

        if( lValue.isError )
        {
            mk.debug(area.Formula, `[toValue] concatExpr: ${concatOp.toString()} => ${lhsValue}, ${rhsValue}`);
            return lValue;
        }

        if( !rValue )
        {
            mk.debug(area.Formula, `[toValue] concatExpr: ${concatOp.toString()} => ${lhsValue}, ${rhsValue}`);
            return EvalErrors.Value;
        }

        if( rValue.isError )
        {
            mk.debug(area.Formula, `[toValue] concatExpr: ${concatOp.toString()} => ${lhsValue}, ${rhsValue}`);
            return rValue;
        }

        const lhsAsString:IValue = CoercionUtils.coerce(lValue, Type.String, null);
        const rhsAsString:IValue = CoercionUtils.coerce(rValue, Type.String, null);

        if( lhsAsString.isError )
        {
            mk.debug(area.Formula, `[toValue] concatExpr: ${concatOp.toString()} => ${lhsAsString}`);
            return lhsAsString;
        }

        if( rhsAsString.isError )
        {
            mk.debug(area.Formula, `[toValue] concatExpr: ${concatOp.toString()} => ${rhsAsString}`);
            return rhsAsString;
        }

        const stringA:string = (lhsAsString as AString).value;
        const stringB:string = (rhsAsString as AString).value;

        const value:IValue = AString.fromString(stringA + stringB);

        mk.debug(area.Formula, `[toValue] FormulaConcatExpression: ${concatOp.toString()} => ${value}`);
        return value;
    }
}

